import type { Collection } from '@tanstack/vue-db'
import type { MaybeRefOrGetter } from 'vue'
import { toValue } from 'vue'

import { useRhythmicLogger } from '#imports'
import type { CollectionAcquisition } from '../collections/CollectionRegistry'

import useCollectionRegistry from '../collections/CollectionRegistry'

const logger = useRhythmicLogger('useCollection')
/**
 * Acquires a collection from the registry using either a table name with filters or a full request object.
 * @typeParam TCollection - The collection type managed by the registry.
 * @param partitionValue - Optional partition/filter value used to scope partitioned collections.
 * @returns The active collection acquisition handle from the registry.
 */
export function useCollection<TCollection extends object>(
  table: string,
  partitionValue?: MaybeRefOrGetter<Record<string, unknown> | string | null | undefined>,
  // collectionType: 'query' | 'electric' = 'electric',
): CollectionAcquisition<Collection<TCollection>> {
  if (!table) throw new Error('A table name is required to acquire a collection.')

  const registry = useCollectionRegistry()

  // ensure there is a builder for the table
  const builderPartitionKey = registry.getBuilderPartitionKey(table)
  const requiresPartition = Boolean(builderPartitionKey)
  const pv = partitionValue !== undefined ? toValue(partitionValue) : undefined

  let where: Record<string, unknown> | undefined
  if (requiresPartition) {
    if (!pv) throw new Error(`A partition value is required to acquire a collection for table "${table}".`)

    if (typeof pv === 'string') {
      // simple string partition -> assign to partition key
      where = { [builderPartitionKey as string]: pv }
    } else if (pv && typeof pv === 'object') {
      // clone the provided object and ensure the partition key is present
      const obj = pv
      const partitionVal = obj[builderPartitionKey as string]
      if (partitionVal == null)
        throw new Error(`Partition object for table "${table}" must include key "${builderPartitionKey}".`)
      where = { ...obj, [builderPartitionKey as string]: partitionVal }
    } else {
      throw new Error(`A valid partition value must be provided to acquire a collection for table "${table}".`)
    }
  } else if (pv && typeof pv === 'object') {
    // allow custom filters for partitionless collections
    where = { ...pv }
  } else if (pv !== undefined && pv !== null) {
    throw new Error(`Partitionless collection "${table}" only accepts object filters.`)
  }

  const request = {
    table,
    ...(where ? { where } : {}),
  }

  return registry.acquire<Collection<TCollection>>(request)
}
